import { type FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'styled-components';

import { DutchieInput } from 'src/components/dutchie-input';
import type { ManualPaymentProcessor } from 'src/queries/v2/payments/manual-payments';

export type ManualPaymentSubmission = {
  amount: number;
  processorId: number;
  processorName: string;
};

type ManualPaymentModalProps = {
  onClose: () => void;
  onSubmit: (submission: ManualPaymentSubmission) => void;
  open: boolean;
  processors: ManualPaymentProcessor[];
  remaining: number;
};

export const ManualPaymentModal: FC<ManualPaymentModalProps> = ({ onClose, onSubmit, open, processors, remaining }) => {
  const [selectedProcessorId, setSelectedProcessorId] = useState<number>();
  const [amount, setAmount] = useState<string>('');
  const amountInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setAmount('');
      // Auto-select the only processor, exactly like register does when there's just one enabled -
      // no reason to make the budtender pick between one option.
      setSelectedProcessorId(processors.length === 1 ? processors[0]?.ManualPaymentProcessorId : undefined);
    }
  }, [open, processors.length]);

  if (!open) {
    return null;
  }

  const selectedProcessor = processors.find((processor) => processor.ManualPaymentProcessorId === selectedProcessorId);

  const isExact = Number(amount || 0) === 0;

  const submit = (submissionAmount: number) => {
    if (!selectedProcessor || submissionAmount <= 0) {
      return;
    }

    onSubmit({
      amount: submissionAmount,
      processorId: selectedProcessor.ManualPaymentProcessorId,
      processorName: selectedProcessor.ManualPaymentProcessorName,
    });
  };

  return createPortal(
    <Overlay onClick={onClose}>
      <Container onClick={(e) => e.stopPropagation()}>
        <Title>Manual Payment</Title>
        {processors.length > 1 && (
          <>
            <SectionLabel>Non-Integrated processor</SectionLabel>
            <ProcessorButtonRow>
              {processors.map((processor) => (
                <ProcessorButton
                  key={processor.ManualPaymentSettingsId}
                  data-testid={
                    selectedProcessorId === processor.ManualPaymentProcessorId
                      ? 'manual-payment-modal-processor-button-selected'
                      : 'manual-payment-modal-processor-button'
                  }
                  selected={selectedProcessorId === processor.ManualPaymentProcessorId}
                  onClick={() => setSelectedProcessorId(processor.ManualPaymentProcessorId)}
                >
                  {processor.ManualPaymentProcessorName}
                </ProcessorButton>
              ))}
            </ProcessorButtonRow>
          </>
        )}
        <SectionLabel>Amount</SectionLabel>
        <InputRow>
          <DutchieInput
            allowNegative={false}
            automationId='manual-payment-modal-amount-input'
            decimalScale={2}
            fixedDecimalScale
            getInputRef={amountInputRef}
            inputMode='decimal'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
            onFocus={() => {
              amountInputRef.current?.setSelectionRange(0, 0);
            }}
            placeholder='...'
            startAdornment={<DollarInput>$</DollarInput>}
            type='number'
            value={amount}
          />
          <ExactOrAddButton
            data-testid='manual-payment-modal-submit-button'
            disabled={!selectedProcessor}
            onClick={() => {
              if (isExact) {
                setAmount(remaining.toString());
                submit(remaining);
              } else {
                submit(Number(amount));
              }
            }}
          >
            {isExact ? 'Exact' : 'Add'}
          </ExactOrAddButton>
        </InputRow>
        <CancelButton data-testid='manual-payment-modal-cancel-button' onClick={onClose}>
          Cancel
        </CancelButton>
      </Container>
    </Overlay>,
    document.body,
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 452px;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  background-color: white;
  border-radius: 20px;
  box-shadow: 0 0 18px rgba(33, 33, 33, 0.2);
`;

const Title = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
`;

const SectionLabel = styled.div`
  color: #2c3236;
  font-size: 0.8125rem;
  line-height: 1.125rem;
  font-weight: 600;
`;

const ProcessorButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ProcessorButton = styled.button<{ selected?: boolean }>`
  padding: 0.9rem 1.25rem;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.875rem;
  border: 1px solid var(--color-gray-30);
  background-color: ${({ selected }) => (selected ? '#2e3538' : 'white')};
  color: ${({ selected }) => (selected ? 'white' : '#2c3236')};
`;

const InputRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ExactOrAddButton = styled.button<{ disabled?: boolean }>`
  background-color: var(--color-gray-20);
  color: #2e3538;
  border: none;
  padding: 0.75rem 1.25rem;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.25rem;
  border-radius: 0.375rem;

  ${({ disabled }) =>
    disabled &&
    `
    background-color: var(--color-gray-70);
    color: var(--color-gray-50);
    `}
`;

const DollarInput = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #a0a8ab;
`;

const CancelButton = styled.button`
  background: none;
  border: none;
  color: #2c3236;
  font-weight: 600;
  text-decoration: underline;
  padding: 0.5rem 0;
  align-self: center;
`;
